import React from "react";
import { Page, Text, View, StyleSheet, Image, Font } from "@react-pdf/renderer";

// Register Roboto font
Font.register({
  family: "Roboto",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-400-normal.woff",
      fontWeight: "normal",
    },
    {
      src: "https://cdn.jsdelivr.net/npm/@fontsource/roboto/files/roboto-latin-700-normal.woff",
      fontWeight: "bold",
    },
  ],
});

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Roboto",
    position: "relative",
    paddingTop: 110,
    paddingBottom: 85,
    fontSize: 12,
  },
  logo2: {
    position: "absolute",
    top: 30,
    left: 30,
    width: 60,
  },
  upperDesign: {
    position: "absolute",
    top: -10,
    right: 0,
    width: 280,
  },
  lowerDesign: {
    position: "absolute",
    bottom: -10,
    right: 0,
    width: 300,
  },
  mainContent: {
    flex: 1,
    marginTop: 0,
    marginBottom: 0,
    paddingHorizontal: 40,
  },
  heading: {
    fontSize: 23,
    fontWeight: "bold",
    paddingBottom: 4,
  },
  heading_bottom: {
    fontSize: 23,
    fontWeight: "bold",
    marginBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
    paddingBottom: 12,
  },
  subheading: {
    fontSize: 16,
    marginBottom: 40,
  },
  table_subheading: {
    fontSize: 13,
    textAlign: "center",
    marginBottom: 12,
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0, // Remove left border
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
    width: "100%",
    minHeight: 30, // Ensure minimum row height
    breakInside: 'avoid', // Prevent row splitting
  },
  headerRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
  },
  cell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 5,
  },
  // Add special styling for the first cell to add left border
  firstCell: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    padding: 5,
  },
  pageNumber_Box: {
    position: "absolute",
    bottom: 20,
    left: 10,
    width: 25,
    height: 25,
    border: "2px solid orange",
    borderRadius: 15,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: 2,
  },
  pageNumber: {
    fontSize: 11,
    color: "#000",
    textAlign: "center",
  },
});

const TablePDF = ({ entrance_exam_guide = [], name = "User", assets, showPageNumber }) => {
  const columns = entrance_exam_guide.length > 0 ? Object.keys(entrance_exam_guide[0]) : [];

  return (
    <Page size="A4" style={styles.page} wrap>
      {/* Fixed assets */}
      <Image src={assets?.upper} style={styles.upperDesign} fixed />
      <Image src={assets?.lower} style={styles.lowerDesign} fixed />
      <Image src={assets?.logo2} style={styles.logo2} fixed />

      {/* Main Content */}
      <View style={styles.mainContent}>
        <Text style={styles.heading}>Your Roadmap to Higher Education:</Text>
        <Text style={styles.heading_bottom}>Education Pathways</Text>
        <Text style={styles.subheading}>
          To enter these career fields, you may need to take specific entrance
          exams. Here's a comparison table:
        </Text>

        <Text style={styles.table_subheading}>
          Education Pathways ({name})
        </Text>

        {/* Table */}
        <View style={styles.table}>
          {/* Header row with fixed prop */}
          <View style={[styles.tableRow, styles.headerRow]} fixed>
            {columns.map((col, i) => (
              <View style={i === 0 ? styles.firstCell : styles.cell} key={i}>
                <Text>
                  {col
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </Text>
              </View>
            ))}
          </View>

          {/* Data rows */}
          {entrance_exam_guide.map((item, index) => (
            <View style={styles.tableRow} key={index} wrap={false}>
              {columns.map((col, i) => (
                <View style={i === 0 ? styles.firstCell : styles.cell} key={i}>
                  <Text>
                    {(() => {
                      const value = item[col];
                      if (Array.isArray(value)) {
                        return value.map(v => {
                          if (typeof v === 'object' && v !== null) {
                            // Extract a readable label from common field names
                            const label = v.exam_name || v.name || v.title || v.label;
                            if (label) {
                              const period = v.exam_period ? ` (${v.exam_period})` : '';
                              return `${label}${period}`;
                            }
                            return Object.values(v).filter(x => typeof x === 'string').join(', ') || JSON.stringify(v);
                          }
                          return v;
                        }).join("\n");
                      } else if (typeof value === 'object' && value !== null) {
                        return Object.entries(value).map(([key, val]) => `${key}: ${val}`).join(", ");
                      } else {
                        return value ?? "";
                      }
                    })()}
                  </Text>
                </View>
              ))}
            </View>
          ))}
        </View>
      </View>

      {/* Footer - page number only */}
      <>
        {showPageNumber && (
          <View style={styles.pageNumber_Box} fixed>
            <Text
              style={styles.pageNumber}
              render={({ pageNumber, totalPages }) => `${pageNumber}`}
              fixed
            />
          </View>
        )}
      </>
    </Page>
  );
};

export default TablePDF;